import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import Sidebar from "../components/Sidebar";
import { downloadEstimatePDF, getEstimatePDFBase64 } from "../components/EstimatePDF";
import { useCompany } from "../hooks/useCompany";
import { errorDetail } from "../utils/apiError";

const API_URL = process.env.REACT_APP_API_URL;

const emptyLine = () => ({ name: "", description: "", quantity: 1, unit_price: "", total: 0 });

const Tip = ({ text, children, className = "" }) => (
  <div className={`relative group/tip ${className}`}>
    {children}
    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover/tip:block z-50 pointer-events-none">
      <div className="bg-gray-800 text-white text-xs rounded-lg px-2.5 py-1.5 shadow-lg w-48 text-center leading-snug">
        {text}
      </div>
      <div className="w-2 h-2 bg-gray-800 rotate-45 mx-auto -mt-1" />
    </div>
  </div>
);

const ItemNameCell = ({ value, catalog, onChange, onSelect, readOnly, placeholder }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const suggestions = (() => {
    const q = value.trim().toLowerCase();
    const pool = q
      ? catalog.filter((c) => c.name.toLowerCase().includes(q))
      : [...catalog].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 5);
    return pool.slice(0, 5);
  })();

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={wrapRef} className="relative w-full">
      <input
        className="w-full border-0 bg-transparent text-sm focus:outline-none font-medium"
        placeholder={placeholder || "Item name *"}
        value={value}
        readOnly={readOnly}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => { if (!readOnly) setOpen(true); }}
      />
      {open && !readOnly && suggestions.length > 0 && (
        <div className="absolute left-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-lg w-64 overflow-hidden">
          {suggestions.map((item) => (
            <button
              key={item.item_id}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                onSelect(item);
                setOpen(false);
              }}
              className="w-full text-left px-3 py-2 hover:bg-blue-50 transition"
            >
              <p className="text-sm font-medium text-gray-800">{item.name}</p>
              <div className="flex justify-between items-center">
                {item.description && (
                  <p className="text-xs text-gray-400 truncate mr-2">{item.description}</p>
                )}
                <p className="text-xs text-blue-600 font-medium whitespace-nowrap">
                  ${parseFloat(item.unit_price).toFixed(2)}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const ClientNameCell = ({ value, clients, onChange, onSelect, readOnly, placeholder }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const suggestions = (() => {
    const q = value.trim().toLowerCase();
    const pool = q
      ? clients.filter((c) => c.name.toLowerCase().includes(q))
      : [...clients].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 5);
    return pool.slice(0, 5);
  })();

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={wrapRef} className="relative w-full">
      <input
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
        placeholder={placeholder || "Client Name *"}
        value={value}
        readOnly={readOnly}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => { if (!readOnly) setOpen(true); }}
      />
      {open && !readOnly && suggestions.length > 0 && (
        <div className="absolute left-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-lg w-full overflow-hidden">
          {suggestions.map((client) => (
            <button
              key={client.client_id}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                onSelect(client);
                setOpen(false);
              }}
              className="w-full text-left px-3 py-2 hover:bg-blue-50 transition"
            >
              <p className="text-sm font-medium text-gray-800">{client.name}</p>
              <p className="text-xs text-gray-400 truncate">
                {[client.city, client.state].filter(Boolean).join(", ")}
                {client.email ? ` · ${client.email}` : ""}
              </p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const UpdateEstimate = () => {
  const auth = useAuth();
  const location = useLocation();
  const { estimate_id } = useParams();
  const { activeCompany } = useCompany();
  const { t } = useTranslation();

  const token = auth.user?.access_token;

  const [estimate, setEstimate] = useState(location.state?.estimate || null);
  const [loading, setLoading] = useState(!location.state?.estimate);
  const [notFound, setNotFound] = useState(false);
  const [viewOnly, setViewOnly] = useState(location.state?.viewOnly ?? true);

  const [catalogItems, setCatalogItems] = useState([]);
  const [clientCatalog, setClientCatalog] = useState([]);
  const [showCatalog, setShowCatalog] = useState(false);
  const [newCatalogItem, setNewCatalogItem] = useState({ name: "", description: "", unit_price: "" });
  const [savingCatalog, setSavingCatalog] = useState(false);

  const [form, setForm] = useState({
    client_name: "",
    client_address: "",
    client_city: "",
    client_state: "",
    client_zip: "",
    client_country: "",
    client_email: "",
    client_phone: "",
    date: "",
    due_date: "",
    notes: "",
    tax_rate: "",
  });

  const [lineItems, setLineItems] = useState([emptyLine()]);
  const [submitting, setSubmitting] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [markingAccepted, setMarkingAccepted] = useState(false);

  // Fetch estimate by ID if not passed via state
  useEffect(() => {
    if (!estimate && estimate_id && token) {
      fetch(`${API_URL}/estimates/${estimate_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((res) => {
          if (!res.ok) throw new Error("Not found");
          return res.json();
        })
        .then((data) => { setEstimate(data); setLoading(false); })
        .catch(() => { setNotFound(true); setLoading(false); });
    } else if (estimate) {
      setLoading(false);
    }
  }, [estimate_id, token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Populate form whenever estimate changes
  useEffect(() => {
    if (estimate) {
      setForm({
        client_name: estimate.client_name || "",
        client_address: estimate.client_address || "",
        client_city: estimate.client_city || "",
        client_state: estimate.client_state || "",
        client_zip: estimate.client_zip || "",
        client_country: estimate.client_country || "",
        client_email: estimate.client_email || "",
        client_phone: estimate.client_phone || "",
        date: estimate.date || "",
        due_date: estimate.due_date || "",
        notes: estimate.notes || "",
        tax_rate: estimate.tax_rate != null ? String(estimate.tax_rate) : "",
      });
      setLineItems(
        estimate.line_items?.length
          ? estimate.line_items.map((li) => ({
              name: li.name || "",
              description: li.description || "",
              quantity: li.quantity,
              unit_price: li.unit_price,
              total: li.total,
            }))
          : [emptyLine()]
      );
    }
  }, [estimate]);

  const fetchCatalog = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/invoice_items/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setCatalogItems(data.sort((a, b) => a.name.localeCompare(b.name)));
      } else {
        setCatalogItems([]);
      }
    } catch {
      setCatalogItems([]);
    }
  }, [token]);

  const fetchClients = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/clients/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setClientCatalog(data);
      } else {
        setClientCatalog([]);
      }
    } catch {
      setClientCatalog([]);
    }
  }, [token]);

  useEffect(() => {
    if (auth.isAuthenticated) { fetchCatalog(); fetchClients(); }
  }, [auth.isAuthenticated, fetchCatalog, fetchClients]);

  const updateLineItem = (index, field, value) => {
    setLineItems((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      const qty = parseFloat(updated[index].quantity) || 0;
      const price = parseFloat(updated[index].unit_price) || 0;
      updated[index].total = qty * price;
      return updated;
    });
  };

  const selectCatalogItem = (index, item) => {
    setLineItems((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        name: item.name,
        description: item.description || updated[index].description,
        unit_price: item.unit_price,
        total: (parseFloat(updated[index].quantity) || 1) * parseFloat(item.unit_price),
      };
      return updated;
    });
  };

  const addLineItem = () => setLineItems((prev) => [...prev, emptyLine()]);

  const removeLineItem = (index) => {
    if (lineItems.length === 1) return;
    setLineItems((prev) => prev.filter((_, i) => i !== index));
  };

  const addFromCatalog = (item) => {
    setLineItems((prev) => [
      ...prev,
      { name: item.name, description: item.description || "", quantity: 1, unit_price: item.unit_price, total: item.unit_price },
    ]);
    setShowCatalog(false);
  };

  const subtotal = lineItems.reduce((s, li) => s + (parseFloat(li.total) || 0), 0);
  const taxRate = parseFloat(form.tax_rate) || 0;
  const taxAmount = (subtotal * taxRate) / 100;
  const totalAmount = subtotal + taxAmount;

  const businessInfo = {
    business_name:    activeCompany?.name    || "",
    business_address: activeCompany?.address || "",
    business_city:    activeCompany?.city    || "",
    business_state:   activeCompany?.state   || "",
    business_zip:     activeCompany?.zip     || "",
    business_phone:   activeCompany?.phone   || "",
    business_email:   activeCompany?.email   || "",
  };

  const autoSaveNewItems = async (items) => {
    const existingNames = new Set(catalogItems.map((c) => c.name.toLowerCase()));
    const seen = new Set();
    const toSave = items.filter((li) => {
      const key = li.name.trim().toLowerCase();
      if (!key || existingNames.has(key) || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    await Promise.all(
      toSave.map((li) =>
        fetch(`${API_URL}/invoice_items`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({
            name: li.name.trim(),
            description: li.description || "",
            unit_price: parseFloat(li.unit_price) || 0,
          }),
        }).catch(() => {})
      )
    );
  };

  const autoSaveNewClient = async (f) => {
    const name = f.client_name.trim();
    if (!name) return;
    const alreadyExists = clientCatalog.some((c) => c.name.toLowerCase() === name.toLowerCase());
    if (alreadyExists) return;
    try {
      await fetch(`${API_URL}/clients`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          address: f.client_address || "",
          city: f.client_city || "",
          state: f.client_state || "",
          zip: f.client_zip || "",
          country: f.client_country || "",
          email: f.client_email || "",
          phone: f.client_phone || "",
        }),
      });
    } catch {}
  };

  const handleSubmit = async () => {
    if (!form.client_name.trim()) { Swal.fire({ icon: "error", title: t("required"), text: t("client_name_required") }); return; }
    if (lineItems.some((li) => !li.name.trim())) { Swal.fire({ icon: "error", title: t("required"), text: t("line_items_name_required") }); return; }

    setSubmitting(true);
    try {
      const payload = {
        ...form,
        tax_rate: taxRate,
        line_items: lineItems.map((li) => ({
          name: li.name,
          description: li.description || "",
          quantity: parseFloat(li.quantity) || 0,
          unit_price: parseFloat(li.unit_price) || 0,
          total: parseFloat(li.total) || 0,
        })),
        subtotal,
        tax_amount: taxAmount,
        total_amount: totalAmount,
        ...businessInfo,
      };

      const res = await fetch(`${API_URL}/estimates/${estimate.estimate_id}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`Failed to update estimate: ${res.status}`);
      const savedEstimate = await res.json();
      await Promise.all([autoSaveNewItems(lineItems), autoSaveNewClient(form)]);
      await Swal.fire({ icon: "success", title: t("saved_title"), text: `Estimate #${savedEstimate.estimate_number} updated.`, timer: 1500, showConfirmButton: false });
      setEstimate(savedEstimate);
      setViewOnly(true);
    } catch (err) {
      Swal.fire({ icon: "error", title: t("error"), text: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveCatalogItem = async () => {
    if (!newCatalogItem.name.trim() || !newCatalogItem.unit_price) {
      Swal.fire({ icon: "error", title: t("required"), text: t("item_name_placeholder") + " & " + t("unit_price_placeholder") });
      return;
    }
    setSavingCatalog(true);
    try {
      const res = await fetch(`${API_URL}/invoice_items`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          name: newCatalogItem.name.trim(),
          description: newCatalogItem.description.trim(),
          unit_price: parseFloat(newCatalogItem.unit_price),
        }),
      });
      if (!res.ok) throw new Error("Failed to save item");
      setNewCatalogItem({ name: "", description: "", unit_price: "" });
      fetchCatalog();
    } catch (err) {
      Swal.fire({ icon: "error", title: t("save_failed"), text: err.message });
    } finally {
      setSavingCatalog(false);
    }
  };

  const handleDeleteCatalogItem = async (itemId) => {
    try {
      await fetch(`${API_URL}/invoice_items/${itemId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      setCatalogItems((prev) => prev.filter((i) => i.item_id !== itemId));
    } catch (err) {
      Swal.fire({ icon: "error", title: t("delete_failed"), text: err.message });
    }
  };

  const handleMarkAccepted = async () => {
    const today = new Date().toISOString().split("T")[0];
    setMarkingAccepted(true);
    try {
      const res = await fetch(`${API_URL}/estimates/${estimate.estimate_id}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ accepted: true, accepted_on: today }),
      });
      if (!res.ok) throw new Error("Failed to update estimate");
      setEstimate(await res.json());
    } catch (err) {
      Swal.fire({ icon: "error", title: "Error", text: err.message });
    } finally {
      setMarkingAccepted(false);
    }
  };

  const handleMarkOpen = async () => {
    setMarkingAccepted(true);
    try {
      const res = await fetch(`${API_URL}/estimates/${estimate.estimate_id}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ accepted: false, accepted_on: "" }),
      });
      if (!res.ok) throw new Error("Failed to update estimate");
      setEstimate(await res.json());
    } catch (err) {
      Swal.fire({ icon: "error", title: "Error", text: err.message });
    } finally {
      setMarkingAccepted(false);
    }
  };

  const withLogo = (doc) => ({ ...doc, business_logo_url: activeCompany?.logo_url || "" });

  const handlePrint = () => downloadEstimatePDF(withLogo(estimate));

  const handleSendEmail = async () => {
    const recipient = estimate.client_email;
    if (!recipient) {
      Swal.fire({ icon: "error", title: t("error"), text: t("no_client_email_estimate") });
      return;
    }
    setSendingEmail(true);
    try {
      const pdf_base64 = await getEstimatePDFBase64(withLogo(estimate));
      const res = await fetch(`${API_URL}/estimates/${estimate.estimate_id}/send-email`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ recipient_email: recipient, pdf_base64 }),
      });
      if (!res.ok) throw new Error(await errorDetail(res, "Failed to send email."));
      Swal.fire({ icon: "success", title: t("email_sent"), text: `${t("estimate_emailed_to")} ${recipient}` });
    } catch (err) {
      Swal.fire({ icon: "error", title: t("error"), text: err.message });
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSignOut = () => {
    auth.signOut();
  };

  const inputClass = `w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 ${viewOnly ? "bg-gray-50" : ""}`;

  if (loading) {
    return (
      <div className="flex min-h-screen bg-gray-100">
        <Sidebar handleSignOut={handleSignOut}>
          <div className="flex flex-col flex-1 items-center justify-center p-6">
            <p className="text-gray-500 text-sm">Loading estimate...</p>
          </div>
        </Sidebar>
      </div>
    );
  }

  if (notFound || !estimate) {
    return (
      <div className="flex min-h-screen bg-gray-100">
        <Sidebar handleSignOut={handleSignOut}>
          <div className="flex flex-col flex-1 items-center justify-center p-6 gap-4">
            <p className="text-gray-700 font-medium">Estimate not found.</p>
            <Link to="/Estimates" className="text-blue-600 underline text-sm">Back to Estimates</Link>
          </div>
        </Sidebar>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gray-100">
      <Sidebar handleSignOut={handleSignOut}>
      <div className="flex flex-col flex-1 p-3 sm:p-6">
        <div className="bg-white shadow-lg rounded-lg p-4 sm:p-8 w-full max-w-4xl mx-auto">

          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
            <div>
              <h1 className="text-xl sm:text-2xl font-semibold text-gray-800">
                Estimate #{estimate.estimate_number}
              </h1>
              {estimate.accepted ? (
                <span className="inline-block mt-1 bg-blue-100 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full">
                  {t("status_accepted")} {estimate.accepted_on ? `· ${estimate.accepted_on}` : ""}
                </span>
              ) : (
                <span className="inline-block mt-1 bg-yellow-100 text-yellow-700 text-xs font-semibold px-3 py-1 rounded-full">
                  {t("status_open")}
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {viewOnly && (
                estimate.accepted ? (
                  <>
                    <button onClick={handleMarkOpen} disabled={markingAccepted}
                      className="flex-1 sm:flex-none bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 text-sm disabled:opacity-50">
                      {markingAccepted ? "…" : "Reopen"}
                    </button>
                    <Tip text="Reopen the estimate to edit it" className="flex-1 sm:flex-none">
                      <button disabled className="w-full bg-gray-100 text-gray-400 px-4 py-2 rounded-lg text-sm cursor-not-allowed">Edit</button>
                    </Tip>
                    <button onClick={handlePrint} className="flex-1 sm:flex-none bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 text-sm">
                      {t("print_save_pdf")}
                    </button>
                    {estimate.client_email ? (
                      <button onClick={handleSendEmail} disabled={sendingEmail}
                        className="flex-1 sm:flex-none bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 text-sm disabled:opacity-50">
                        {sendingEmail ? t("sending") : t("send_email_btn")}
                      </button>
                    ) : (
                      <Tip text="Add a client email to enable sending" className="flex-1 sm:flex-none">
                        <button disabled className="w-full bg-gray-100 text-gray-400 px-4 py-2 rounded-lg text-sm cursor-not-allowed">{t("send_email_btn")}</button>
                      </Tip>
                    )}
                  </>
                ) : (
                  <>
                    <button onClick={() => setViewOnly(false)}
                      className="flex-1 sm:flex-none bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 text-sm">
                      Edit
                    </button>
                    <button onClick={handleMarkAccepted} disabled={markingAccepted}
                      className="flex-1 sm:flex-none bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 text-sm disabled:opacity-50">
                      {markingAccepted ? "…" : "Accept"}
                    </button>
                    <button onClick={handlePrint} className="flex-1 sm:flex-none bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 text-sm">
                      {t("print_save_pdf")}
                    </button>
                    {estimate.client_email ? (
                      <button onClick={handleSendEmail} disabled={sendingEmail}
                        className="flex-1 sm:flex-none bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 text-sm disabled:opacity-50">
                        {sendingEmail ? t("sending") : t("send_email_btn")}
                      </button>
                    ) : (
                      <Tip text="Add a client email to enable sending" className="flex-1 sm:flex-none">
                        <button disabled className="w-full bg-gray-100 text-gray-400 px-4 py-2 rounded-lg text-sm cursor-not-allowed">{t("send_email_btn")}</button>
                      </Tip>
                    )}
                  </>
                )
              )}
              <Link to="/Estimates" className="flex-1 sm:flex-none">
                <button className="w-full bg-gray-400 text-white px-4 py-2 rounded-lg hover:bg-gray-500 text-sm">{t("back_btn")}</button>
              </Link>
            </div>
          </div>

          {/* Business + Client Info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-8 mb-6">
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase mb-2">{t("from_label")}</p>
              <div className="text-sm text-gray-700 space-y-1">
                {activeCompany?.logo_url && (
                  <img src={activeCompany.logo_url} alt="Business logo" className="h-12 object-contain mb-1" />
                )}
                <p className="font-semibold">{businessInfo.business_name || t("your_business_name")}</p>
                {businessInfo.business_address && <p>{businessInfo.business_address}</p>}
                {(businessInfo.business_city || businessInfo.business_state) && (
                  <p>{businessInfo.business_city}{businessInfo.business_city && businessInfo.business_state ? ", " : ""}{businessInfo.business_state} {businessInfo.business_zip}</p>
                )}
                {businessInfo.business_phone && <p>{businessInfo.business_phone}</p>}
                {businessInfo.business_email && <p>{businessInfo.business_email}</p>}
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase mb-2">{t("prepared_for_label")}</p>
              <div className="space-y-2">
                <ClientNameCell
                  value={form.client_name}
                  clients={clientCatalog}
                  readOnly={viewOnly}
                  placeholder={t("col_client") + " *"}
                  onChange={(val) => setForm((f) => ({ ...f, client_name: val }))}
                  onSelect={(client) =>
                    setForm((f) => ({
                      ...f,
                      client_name: client.name,
                      client_address: client.address || f.client_address,
                      client_city: client.city || f.client_city,
                      client_state: client.state || f.client_state,
                      client_zip: client.zip || f.client_zip,
                      client_country: client.country || f.client_country,
                      client_email: client.email || f.client_email,
                      client_phone: client.phone || f.client_phone,
                    }))
                  }
                />
                <input className={inputClass} placeholder={t("street_address")} value={form.client_address}
                  onChange={(e) => setForm((f) => ({ ...f, client_address: e.target.value }))} readOnly={viewOnly} />
                <div className="grid grid-cols-2 gap-2">
                  <input className={inputClass} placeholder={t("city")} value={form.client_city}
                    onChange={(e) => setForm((f) => ({ ...f, client_city: e.target.value }))} readOnly={viewOnly} />
                  <input className={inputClass} placeholder={t("state")} value={form.client_state}
                    onChange={(e) => setForm((f) => ({ ...f, client_state: e.target.value }))} readOnly={viewOnly} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <input className={inputClass} placeholder={t("zip")} value={form.client_zip}
                    onChange={(e) => setForm((f) => ({ ...f, client_zip: e.target.value }))} readOnly={viewOnly} />
                  <input className={inputClass} placeholder="Country" value={form.client_country}
                    onChange={(e) => setForm((f) => ({ ...f, client_country: e.target.value }))} readOnly={viewOnly} />
                </div>
                <input className={inputClass} placeholder={t("email")} value={form.client_email}
                  onChange={(e) => setForm((f) => ({ ...f, client_email: e.target.value }))} readOnly={viewOnly} />
                <input className={inputClass} placeholder={t("phone")} value={form.client_phone}
                  onChange={(e) => setForm((f) => ({ ...f, client_phone: e.target.value }))} readOnly={viewOnly} />
              </div>
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">{t("estimate_date_label")}</label>
              <input type="date" className={inputClass} value={form.date}
                onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} readOnly={viewOnly} />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">{t("valid_until_label")}</label>
              <input type="date" className={inputClass} value={form.due_date}
                onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))} readOnly={viewOnly} />
            </div>
          </div>

          {/* Line Items */}
          <div className="mb-4">
            {showCatalog && !viewOnly && (
              <div className="mb-4 border border-blue-100 rounded-lg p-4 bg-blue-50">
                <p className="text-xs font-semibold text-blue-700 mb-1">{t("saved_items_catalog")}</p>
                <p className="text-xs text-gray-400 mb-3">{t("catalog_autosave_hint")}</p>
                {catalogItems.length === 0 ? (
                  <p className="text-xs text-gray-500 mb-3">{t("no_saved_items")}</p>
                ) : (
                  <div className="space-y-1 mb-3 max-h-40 overflow-y-auto">
                    {catalogItems.map((item) => (
                      <div key={item.item_id} className="flex items-center justify-between bg-white border border-blue-100 rounded px-3 py-2 text-sm">
                        <div>
                          <span className="font-medium">{item.name}</span>
                          {item.description && <span className="text-gray-500 ml-2 text-xs">{item.description}</span>}
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-blue-700 font-medium">${parseFloat(item.unit_price).toFixed(2)}</span>
                          <button onClick={() => addFromCatalog(item)}
                            className="text-xs bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600">{t("add_row_btn").replace("+ ", "")}</button>
                          <button onClick={() => handleDeleteCatalogItem(item.item_id)}
                            className="text-xs text-red-400 hover:text-red-600">✕</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="border-t border-blue-100 pt-3">
                  <p className="text-xs text-blue-600 font-medium mb-2">{t("add_item_manually")}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <input className="border border-gray-300 rounded px-2 py-1 text-xs" placeholder={t("item_name_placeholder")}
                      value={newCatalogItem.name} onChange={(e) => setNewCatalogItem((n) => ({ ...n, name: e.target.value }))} />
                    <input className="border border-gray-300 rounded px-2 py-1 text-xs" placeholder={t("col_description")}
                      value={newCatalogItem.description} onChange={(e) => setNewCatalogItem((n) => ({ ...n, description: e.target.value }))} />
                    <div className="flex gap-1">
                      <input type="number" className="border border-gray-300 rounded px-2 py-1 text-xs w-full" placeholder={t("unit_price_placeholder")}
                        value={newCatalogItem.unit_price} onChange={(e) => setNewCatalogItem((n) => ({ ...n, unit_price: e.target.value }))} />
                      <button onClick={handleSaveCatalogItem} disabled={savingCatalog}
                        className="bg-blue-500 text-white text-xs px-2 rounded hover:bg-blue-600 disabled:opacity-50 whitespace-nowrap">
                        {t("save")}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Line items — desktop table */}
            <div className="hidden sm:block">
              <table className="w-full text-sm border border-gray-200 rounded-lg">
                <thead>
                  <tr className="bg-gray-100 text-gray-600 text-xs uppercase">
                    <th className="px-3 py-2 border text-left w-36">{t("col_item")}</th>
                    <th className="px-3 py-2 border text-left">{t("col_description")}</th>
                    <th className="px-3 py-2 border text-right w-20">{t("col_qty")}</th>
                    <th className="px-3 py-2 border text-right w-28">{t("col_unit_price")}</th>
                    <th className="px-3 py-2 border text-right w-28">{t("col_total")}</th>
                    {!viewOnly && <th className="px-3 py-2 border w-10"></th>}
                  </tr>
                </thead>
                <tbody>
                  {lineItems.map((li, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-3 py-2 border overflow-visible">
                        <ItemNameCell value={li.name} catalog={catalogItems} readOnly={viewOnly}
                          placeholder={t("item_name_placeholder")}
                          onChange={(val) => updateLineItem(i, "name", val)}
                          onSelect={(item) => selectCatalogItem(i, item)} />
                      </td>
                      <td className="px-3 py-2 border">
                        <input className="w-full border-0 bg-transparent text-sm focus:outline-none text-gray-500"
                          placeholder={t("description_optional")} value={li.description}
                          onChange={(e) => updateLineItem(i, "description", e.target.value)} readOnly={viewOnly} />
                      </td>
                      <td className="px-3 py-2 border">
                        <input type="number" className="w-full border-0 bg-transparent text-sm text-right focus:outline-none"
                          min="0" value={li.quantity}
                          onChange={(e) => updateLineItem(i, "quantity", e.target.value)} readOnly={viewOnly} />
                      </td>
                      <td className="px-3 py-2 border">
                        <input type="number" className="w-full border-0 bg-transparent text-sm text-right focus:outline-none"
                          min="0" step="0.01" placeholder="0.00" value={li.unit_price}
                          onChange={(e) => updateLineItem(i, "unit_price", e.target.value)} readOnly={viewOnly} />
                      </td>
                      <td className="px-3 py-2 border text-right font-medium">${(parseFloat(li.total) || 0).toFixed(2)}</td>
                      {!viewOnly && (
                        <td className="px-3 py-2 border text-center">
                          <button onClick={() => removeLineItem(i)} className="text-red-400 hover:text-red-600 text-lg leading-none">×</button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Line items — mobile cards */}
            <div className="sm:hidden space-y-3">
              {lineItems.map((li, i) => (
                <div key={i} className="border border-gray-200 rounded-xl p-3 bg-gray-50">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex-1 min-w-0">
                      <ItemNameCell value={li.name} catalog={catalogItems} readOnly={viewOnly}
                        placeholder={t("item_name_placeholder")}
                        onChange={(val) => updateLineItem(i, "name", val)}
                        onSelect={(item) => selectCatalogItem(i, item)} />
                    </div>
                    {!viewOnly && (
                      <button onClick={() => removeLineItem(i)} className="text-red-400 hover:text-red-600 text-xl leading-none flex-shrink-0">×</button>
                    )}
                  </div>
                  <input className="w-full border-0 bg-transparent text-sm text-gray-500 focus:outline-none mb-2"
                    placeholder={t("description_optional")} value={li.description}
                    onChange={(e) => updateLineItem(i, "description", e.target.value)} readOnly={viewOnly} />
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <label className="text-xs text-gray-400 block mb-0.5">{t("col_qty")}</label>
                      <input type="number" className="w-full border border-gray-200 rounded px-2 py-1 text-sm text-right focus:outline-none bg-white"
                        min="0" value={li.quantity}
                        onChange={(e) => updateLineItem(i, "quantity", e.target.value)} readOnly={viewOnly} />
                    </div>
                    <div className="flex-1">
                      <label className="text-xs text-gray-400 block mb-0.5">{t("col_unit_price")}</label>
                      <input type="number" className="w-full border border-gray-200 rounded px-2 py-1 text-sm text-right focus:outline-none bg-white"
                        min="0" step="0.01" placeholder="0.00" value={li.unit_price}
                        onChange={(e) => updateLineItem(i, "unit_price", e.target.value)} readOnly={viewOnly} />
                    </div>
                    <div className="flex-1 text-right">
                      <label className="text-xs text-gray-400 block mb-0.5">{t("col_total")}</label>
                      <p className="text-sm font-bold text-gray-800 py-1">${(parseFloat(li.total) || 0).toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {!viewOnly && (
              <div className="mt-2 mb-2 flex gap-2">
                <button onClick={addLineItem}
                  className="text-sm bg-blue-100 text-blue-700 px-3 py-1.5 rounded hover:bg-blue-200 font-medium">
                  {t("add_row_btn")}
                </button>
                <button onClick={() => setShowCatalog((s) => !s)}
                  className="text-sm text-blue-600 border border-blue-200 px-3 py-1.5 rounded hover:bg-blue-50 font-medium">
                  {showCatalog ? t("hide_catalog") : t("manage_catalog")}
                </button>
              </div>
            )}
          </div>

          {/* Totals + Tax */}
          <div className="flex justify-end mb-6">
            <div className="w-full sm:w-64 space-y-2 text-sm">
              <div className="flex justify-between text-gray-600">
                <span>{t("subtotal_label")}</span>
                <span>${subtotal.toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between text-gray-600">
                <span>{t("tax_label")}</span>
                <div className="flex items-center gap-1">
                  {!viewOnly ? (
                    <input type="number" min="0" max="100" step="0.1" placeholder="0"
                      className="w-16 border border-gray-300 rounded px-2 py-1 text-xs text-right focus:outline-none focus:ring-1 focus:ring-blue-400"
                      value={form.tax_rate} onChange={(e) => setForm((f) => ({ ...f, tax_rate: e.target.value }))} />
                  ) : (
                    <span>{taxRate}%</span>
                  )}
                  {!viewOnly && <span className="text-xs text-gray-400">%</span>}
                  <span className="ml-2">${taxAmount.toFixed(2)}</span>
                </div>
              </div>
              <div className="flex justify-between font-bold text-gray-800 border-t pt-2 text-base">
                <span>{t("total_label")}</span>
                <span>${totalAmount.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="mb-6">
            <label className="text-xs font-medium text-gray-500 mb-1 block">{t("notes_label")}</label>
            <textarea rows={3} className={inputClass} placeholder={t("notes_placeholder")}
              value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} readOnly={viewOnly} />
          </div>

          {/* Actions */}
          {!viewOnly && (
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
              <button
                onClick={() => setViewOnly(true)}
                className="w-full sm:w-auto bg-gray-400 text-white px-5 py-2 rounded-lg hover:bg-gray-500 text-sm"
              >
                {t("cancel")}
              </button>
              <button onClick={handleSubmit} disabled={submitting}
                className="w-full sm:w-auto bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 text-sm font-medium disabled:opacity-50">
                {submitting ? t("saving") : "Save Changes"}
              </button>
            </div>
          )}
        </div>
      </div>
      </Sidebar>
    </div>
  );
};

export default UpdateEstimate;
